import AssignTasksToSelfButton from "@/components/Forms/SubForms/AssignTasksToSelfButton.tsx";
import BulkAssignTasksButton from "@/components/Forms/SubForms/BulkAssignTasksButton.tsx";
import MrtDashboardCaseTasks from "@/components/Table/MrtDashboardCaseTasks.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useFetchMrtDashboardTasks } from "@/queries/useFetchMrtDashboardTasks.ts";
import type { CaseTask, CaseTaskWithDueDate } from "@/types/caseTask.ts";
import ClearIcon from "@mui/icons-material/Clear";
import {
    Box,
    CircularProgress,
    Grid2 as Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Dayjs } from "dayjs";
import { type ChangeEvent, type ReactNode, useEffect, useState } from "react";

const filteredTasksWithDueDates = (
    tasks: CaseTask[],
    dateStart: Dayjs | null,
    dateEnd: Dayjs | null,
    selectedClient: string,
    dashboardVersion: string,
): CaseTaskWithDueDate[] => {
    let filteredTasks: CaseTaskWithDueDate[] = tasks.map((task) => {
        // For tasks with duration = 0, use a far future date for sorting purposes
        const dueDate =
            task.duration === 0
                ? new Date(9999, 11, 31) // Far future date for tasks without due dates
                : new Date(task.metaCreatedWhen.getTime() + task.duration * 24 * 60 * 60 * 1000);
        return {
            ...task,
            dueDate,
            overdue:
                task.duration === 0 ? false : dueDate < new Date() && task.status !== "Complete",
        };
    });

    filteredTasks = filteredTasks.filter((caseTask) => {
        return caseTask.coreRoles.includes(dashboardVersion);
    });

    if (dateStart) {
        filteredTasks = filteredTasks.filter((task) => {
            return (
                dateStart.startOf("day").isBefore(task.dueDate) ||
                dateStart.startOf("day").isBefore(task.metaCreatedWhen)
            );
        });
    }

    if (dateEnd) {
        filteredTasks = filteredTasks.filter((task) => {
            return (
                dateEnd.endOf("day").isAfter(task.dueDate) ||
                dateEnd.endOf("day").isAfter(task.metaCreatedWhen)
            );
        });
    }

    if (selectedClient) {
        filteredTasks = filteredTasks.filter((task) => {
            return task.case.clientName === selectedClient;
        });
    }

    // order tasks by due
    filteredTasks.sort((a, b) => {
        return a.dueDate.getTime() - b.dueDate.getTime();
    });

    return filteredTasks;
};

type Props = {
    taskDashboard: boolean;
};

const CaseTasksDashboard = ({ taskDashboard }: Props): ReactNode => {
    const { employee } = useEmployee();
    const [filteredTasks, setFilteredTasks] = useState<CaseTaskWithDueDate[] | null>(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [dateStart, setDateStart] = useState<Dayjs | null>(null);
    const [dateEnd, setDateEnd] = useState<Dayjs | null>(null);
    const [selectedClient, setSelectedClient] = useState("");

    const { data, isError, isLoading, refetch, error } = useFetchMrtDashboardTasks(globalFilter);

    const uniqueClients =
        filteredTasks === null
            ? []
            : [
                  ...new Set(
                      filteredTasks
                          .filter((task) => !task.overdue)
                          .map((task) => task.case.clientName),
                  ),
              ];

    useEffect(() => {
        if (data?.data) {
            setFilteredTasks(
                filteredTasksWithDueDates(
                    data.data,
                    dateStart,
                    dateEnd,
                    selectedClient,
                    employee?.dashboardVersion ?? "",
                ),
            );
        }
    }, [data, dateStart, dateEnd, selectedClient]);

    useEffect(() => {
        void refetch();
    }, [refetch, taskDashboard]);

    return (
        <Box sx={{ pb: 3 }}>
            <Paper elevation={0}>
                <Stack
                    sx={{
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 1,
                        py: 2,
                    }}
                >
                    <Paper sx={{ px: 1 }}>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                gap: 1,
                                py: 2,
                            }}
                        >
                            <TextField
                                label={"Search Cases"}
                                value={globalFilter}
                                onChange={(event) => {
                                    setGlobalFilter(event.target.value);
                                }}
                                sx={{
                                    width: "50%",
                                }}
                                slotProps={{
                                    input: {
                                        endAdornment: globalFilter && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setGlobalFilter("")}
                                                    edge="end"
                                                    aria-label="clear search"
                                                >
                                                    <ClearIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />

                            <TextField
                                select
                                value={selectedClient}
                                name={"selectedClient"}
                                label={"Select Client"}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    setSelectedClient(event.target.value);
                                }}
                                sx={{
                                    width: "50%",
                                }}
                            >
                                <MenuItem key={"blank"} value={""} component={"li"}>
                                    None
                                </MenuItem>
                                {uniqueClients.map((tag) => (
                                    <MenuItem key={tag} value={tag ?? ""} component={"li"}>
                                        {tag}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                justifyContent: "center",
                                mb: 2,
                            }}
                        >
                            <DatePicker
                                label="Start Date"
                                value={dateStart}
                                onChange={(newValue) => setDateStart(newValue)}
                                sx={{
                                    width: "150px",
                                }}
                            />
                            <DatePicker
                                label="End Date"
                                value={dateEnd}
                                onChange={(newValue) => setDateEnd(newValue)}
                                sx={{
                                    ml: 1,
                                    width: "150px",
                                }}
                            />
                        </Stack>
                    </Paper>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 2,
                        }}
                    >
                        <Typography variant={"h5"}>My Tasks</Typography>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                gap: 1,
                                py: 2,
                            }}
                        >
                            <BulkAssignTasksButton />
                            <AssignTasksToSelfButton />
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>
            {isError && (
                <Grid key={"no-tasks"} sx={{ mb: 2 }}>
                    <Typography variant={"h6"}>{error.message}</Typography>
                </Grid>
            )}
            {isLoading && (
                <Grid key={"no-tasks"} sx={{ mb: 2, textAlign: "center" }}>
                    <CircularProgress />
                </Grid>
            )}
            {filteredTasks && filteredTasks.length === 0 && (
                <Grid key={"no-tasks"} sx={{ mb: 2, textAlign: "center" }}>
                    <Typography variant={"h6"}>No tasks found</Typography>
                </Grid>
            )}
            {filteredTasks && filteredTasks.filter((task) => task.overdue).length > 0 && (
                <Grid key={"overdue"} sx={{ mb: 2 }}>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            pb: 2,
                            pr: 2,
                        }}
                    >
                        <Typography variant={"h6"} sx={{ color: "red", fontWeight: 800 }}>
                            Overdue{" "}
                        </Typography>
                    </Stack>
                    <Box sx={{ mb: 4 }}>
                        <MrtDashboardCaseTasks
                            data={filteredTasks.filter((task) => task.overdue)}
                        />
                    </Box>
                </Grid>
            )}
            {filteredTasks &&
                uniqueClients.map((clientName) => (
                    <Grid key={`tasks-${clientName}`} sx={{ mb: 2 }}>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                justifyContent: "flex-end",
                                py: 2,
                                pr: 2,
                            }}
                        >
                            <Typography variant={"h6"} sx={{ mb: 1 }}>
                                {clientName}
                            </Typography>
                        </Stack>
                        <Box sx={{ mb: 4 }}>
                            <MrtDashboardCaseTasks
                                data={filteredTasks.filter(
                                    (task) => !task.overdue && task.case.clientName === clientName,
                                )}
                            />
                        </Box>
                    </Grid>
                ))}
        </Box>
    );
};

export default CaseTasksDashboard;
